import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "../config/api";
import { trackEvent } from "../utils/analytics";
import { startSubscriptionFlow } from "../utils/premiumAccess";
import {
  formatOfferCountdown,
  getCampaignRemainingMs,
  getServerTimeOffset,
  isCampaignActive,
} from "../utils/nationalDayOffer";

const POPUP_INITIAL_DELAY_MS = 8000;
const POPUP_RETRY_DELAY_MS = 15000;
const getSeenKey = (campaign = {}) =>
  `darbak:campaign:${campaign?.id || "unknown"}:${campaign?.noticeVersion || campaign?.endsAt || "v1"}:seen`;

const hasBlockingAttentionLayer = () =>
  Boolean(
    document.querySelector(
      [
        '[role="dialog"][aria-modal="true"]',
        ".subscription-reminder-overlay",
        ".subscription-reminder-bar",
        ".premium-access-overlay",
      ].join(",")
    )
  );

const isCampaignResponse = (campaign) =>
  campaign?.id === "national-day-90d-960" && campaign?.planId === "one_time_90";

export default function NationalDayOffer() {
  const navigate = useNavigate();
  const location = useLocation();
  const [campaign, setCampaign] = useState(null);
  const [serverOffset, setServerOffset] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [showPopup, setShowPopup] = useState(false);
  const [didExpire, setDidExpire] = useState(false);
  const wasActiveRef = useRef(false);
  const popupTimerRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    fetch(`${API_BASE_URL}/api/subscriptions/plans`)
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!mounted || !isCampaignResponse(payload?.campaign)) return;
        setCampaign(payload.campaign);
        setServerOffset(getServerTimeOffset(payload.serverNow));
      })
      .catch(() => null);

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const serverNow = now + serverOffset;
  const isActive = isCampaignActive(campaign, serverNow);
  const countdown = formatOfferCountdown(getCampaignRemainingMs(campaign, serverNow));

  useEffect(() => {
    if (!campaign) return;
    if (wasActiveRef.current && !isActive) setDidExpire(true);
    wasActiveRef.current = isActive;
  }, [campaign, isActive]);

  useEffect(() => {
    if (!isActive || !campaign?.id) return;

    const clearPopupTimer = () => {
      if (popupTimerRef.current) {
        window.clearTimeout(popupTimerRef.current);
        popupTimerRef.current = null;
      }
    };

    const tryToShowPopup = () => {
      if (document.visibilityState !== "visible" || hasBlockingAttentionLayer()) {
        popupTimerRef.current = window.setTimeout(tryToShowPopup, POPUP_RETRY_DELAY_MS);
        return;
      }

      try {
        const seenKey = getSeenKey(campaign);
        if (window.localStorage.getItem(seenKey)) return;
        window.localStorage.setItem(seenKey, "1");
        trackEvent("national_day_popup_shown", {
          metadata: { campaignId: campaign.id },
        });
        setShowPopup(true);
      } catch {
        // If storage is unavailable, keep the campaign non-blocking.
      }
    };

    try {
      if (window.localStorage.getItem(getSeenKey(campaign))) return undefined;
      popupTimerRef.current = window.setTimeout(tryToShowPopup, POPUP_INITIAL_DELAY_MS);
    } catch {
      // If storage is unavailable, keep the campaign non-blocking.
    }

    return clearPopupTimer;
  }, [campaign, isActive]);

  useEffect(() => {
    if (!didExpire) return undefined;
    const timer = window.setTimeout(() => setDidExpire(false), 7000);
    return () => window.clearTimeout(timer);
  }, [didExpire]);

  const goToSubscribe = (source = "popup") => {
    trackEvent(
      source === "countdown_bar"
        ? "national_day_countdown_clicked"
        : "national_day_popup_cta_clicked",
      { metadata: { campaignId: campaign?.id || "national-day-90d-960" } }
    );
    setShowPopup(false);
    startSubscriptionFlow({ planId: "one_time_90", source: "national_day_campaign", campaignId: campaign?.id, navigate });
  };

  if (
    location.pathname.startsWith("/company") ||
    location.pathname.startsWith("/p/") ||
    location.pathname.startsWith("/subscribe")
  ) {
    return null;
  }

  return (
    <>
      {isActive && (
        <button
          type="button"
          className="national-day-offer-bar"
          onClick={() => goToSubscribe("countdown_bar")}
          dir="rtl"
        >
          <span className="national-day-offer-bar-desktop">
            عرض اليوم الوطني 🇸🇦 <i /> 3 أشهر بـ 9.60 بدل 15 ريال <i /> باقي {countdown}
          </span>
          <span className="national-day-offer-bar-mobile">
            اليوم الوطني 🇸🇦 3 أشهر بـ9.60 <i /> باقي {countdown}
          </span>
        </button>
      )}

      {didExpire && (
        <div className="national-day-offer-expired" role="status" dir="rtl">
          انتهى عرض اليوم الوطني وعاد سعر باقة الثلاثة أشهر إلى 15 ريال.
        </div>
      )}

      {showPopup && isActive && (
        <div className="national-day-offer-backdrop" role="presentation">
          <section
            className="national-day-offer-popup"
            role="dialog"
            aria-modal="true"
            aria-labelledby="national-day-offer-title"
            dir="rtl"
          >
            <span className="national-day-offer-eyebrow">🇸🇦 عرض اليوم الوطني</span>
            <h2 id="national-day-offer-title">3 أشهر في دربك بـ <strong>9.60 ريال</strong></h2>
            <div className="national-day-offer-price-note">
              <del>15 ريال</del>
              <span>وفّر 5.40 ريال</span>
            </div>
            <p>استفد من دربك طوال موسم التدريب: تجارب الطلاب، الفرص، المقابلات وأدوات سيرتي في مكان واحد.</p>
            <strong className="national-day-offer-countdown">ينتهي العرض بعد {countdown}</strong>
            <small className="national-day-offer-limit">العرض لفترة محدودة فقط.</small>
            <button type="button" className="national-day-offer-primary" onClick={() => goToSubscribe("popup")}>
              اشترك الآن بـ 9.60 ريال
            </button>
            <span className="national-day-offer-payment-note">دفعة واحدة • 3 أشهر</span>
            <button type="button" className="national-day-offer-later" onClick={() => setShowPopup(false)}>
              لاحقًا
            </button>
          </section>
        </div>
      )}
    </>
  );
}
